
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaintenanceTaskSchedulerDataService } from './maintenance-task-scheduler-data.service';
import { ScheduledMaintenanceTaskCreateRequest } from './scheduled-maintenance-task-create-request.model';
import { ScheduledMaintenanceTask } from './scheduled-maintenance-task.model';
import { Tenant } from '@app/Models/tenant';
import { ToastMessageType, ToastService } from '../Common';
import { Observable, Subscription } from 'rxjs';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatStepper, MatStep, MatStepperNext, MatStepperPrevious } from '@angular/material/stepper';
import { MatFormField, MatHint, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { TenantsSelectionComponent } from './tenants-selection/tenants-selection.component';
import { MaintenanceTaskConfigComponent } from './maintenance-task-config.component';
import { MaintenanceRecurrenceComponent, ScheduleMode } from './recurrence/maintenance-recurrence.component';
import { MaintenanceSchedulePreviewComponent } from './preview/maintenance-schedule-preview.component';
import {
    RecurringScheduledMaintenanceTaskOccurrencePreview,
    RecurringScheduledMaintenanceTaskSeriesCreateRequest
} from './scheduled-maintenance-recurrence.model';

@Component({
    selector: 'maintenance-task',
    templateUrl: './maintenance-task.component.html',
    imports: [MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatStepper, MatStep, FormsModule, ReactiveFormsModule, MatFormField, MatHint, MatInput, MatButton, MatStepperNext, TenantsSelectionComponent, MatStepperPrevious, MaintenanceTaskConfigComponent, MaintenanceRecurrenceComponent, MaintenanceSchedulePreviewComponent]
})
export class MaintenanceTaskComponent implements OnDestroy {
    static readonly selectAllOptionId = '00000000-0000-0000-0000-000000000000';

    @Input() task: ScheduledMaintenanceTask;
    @Input() allTenants$: Observable<Tenant[]>;
    @Output('submit') submitted: EventEmitter<string> = new EventEmitter<string>();

    tenantSelectionStep = new UntypedFormControl('');
    selectedTenants: Tenant[] = [];
    scheduleMode: ScheduleMode = 'OneTime';
    previewOccurrences: RecurringScheduledMaintenanceTaskOccurrencePreview[] = [];
    startDateStep = this.formBuilder.group({
        startdate: ['', [Validators.required]],
    });
    private subscription: Subscription;
    private previewSubscription: Subscription;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private migrationToolDataService: MaintenanceTaskSchedulerDataService,
        private toastService: ToastService) {
    }

    getCreatorTimeZoneId(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    }

    onSelectedTenantsChange(tenants: Tenant[]): void {
        this.selectedTenants = tenants;
        if (tenants?.length > 0) {
            this.tenantSelectionStep.reset();
        } else {
            this.tenantSelectionStep.setErrors({ required: true });
        }
        this.tenantSelectionStep.markAllAsTouched();
    }

    onScheduleModeChanged(scheduleMode: ScheduleMode): void {
        this.scheduleMode = scheduleMode;
    }

    submit(): void {
        if (this.startDateStep.invalid) {
            this.startDateStep.markAllAsTouched();
            return;
        }

        const startDate = new Date(this.startDateStep.value.startdate);

        const request = {
            StartDate: startDate,
            TimeZoneId: this.getCreatorTimeZoneId(),
            TaskConfiguration: this.task.DefaultConfiguration,
            TenantIds: this.selectedTenantIds()
        } as ScheduledMaintenanceTaskCreateRequest;

        this.subscription = this.migrationToolDataService.createScheduledMaintenanceTask(this.task.TaskTypeName, request)
            .subscribe(
                () => {
                    this.toastService.sendMessage(
                        'Success',
                        `${this.task.Name} was scheduled successfully`,
                        ToastMessageType.success
                    );
                    this.submitted.emit('task scheduled');
                },
                (response) => this.showError('Error trying to schedule', response));
    }

    previewRecurring(recurrence: Omit<RecurringScheduledMaintenanceTaskSeriesCreateRequest, 'TaskConfiguration' | 'TenantIds'>): void {
        const request = this.buildRecurringRequest(recurrence);

        if (this.previewSubscription) {
            this.previewSubscription.unsubscribe();
        }

        this.previewSubscription = this.migrationToolDataService.previewRecurringScheduledMaintenanceTask(this.task.TaskTypeName, request)
            .subscribe(
                occurrences => this.previewOccurrences = occurrences,
                (response) => this.showError('Error trying to preview recurring schedule', response));
    }

    submitRecurring(recurrence: Omit<RecurringScheduledMaintenanceTaskSeriesCreateRequest, 'TaskConfiguration' | 'TenantIds'>): void {
        const request = this.buildRecurringRequest(recurrence);

        this.subscription = this.migrationToolDataService.createRecurringScheduledMaintenanceTask(this.task.TaskTypeName, request)
            .subscribe(
                () => {
                    this.toastService.sendMessage(
                        'Success',
                        `${this.task.Name} recurring schedule was created successfully`,
                        ToastMessageType.success
                    );
                    this.submitted.emit('recurring task scheduled');
                },
                (response) => this.showError('Error trying to schedule', response));
    }

    private showError(title: string, response: any): void {
        this.toastService.sendMessage(title, this.errorMessage(response), ToastMessageType.error);
    }

    private errorMessage(response: any): string {
        if (typeof response?.error === 'string') {
            return response.error;
        }

        return response?.error?.Message || response?.error?.message || response?.message || 'Unexpected error';
    }

    private selectedTenantIds(): string[] {
        const tenantIds = this.selectedTenants.map(tenant => tenant.TenantId);

        if (tenantIds.indexOf(MaintenanceTaskComponent.selectAllOptionId) >= 0) {
            return [];
        }

        return tenantIds;
    }

    private buildRecurringRequest(
        recurrence: Omit<RecurringScheduledMaintenanceTaskSeriesCreateRequest, 'TaskConfiguration' | 'TenantIds'>
    ): RecurringScheduledMaintenanceTaskSeriesCreateRequest {
        return {
            ...recurrence,
            TaskConfiguration: this.task.DefaultConfiguration,
            TenantIds: this.selectedTenantIds()
        };
    }

    ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        if (this.previewSubscription) {
            this.previewSubscription.unsubscribe();
        }
    }
}
